/**
 * Polyfill for Screen Orientation API on platforms where window.screen.orientation is missing
 * (e.g. iOS Safari / WKWebView) to prevent crashes in frameworks like Quasar Screen plugin.
 */
if (typeof window !== "undefined" && typeof window.screen !== "undefined") {
    const isOrientationMissing =
        !window.screen.orientation ||
        typeof window.screen.orientation !== "object" ||
        typeof window.screen.orientation.addEventListener !== "function";

    if (isOrientationMissing) {
        const getOrientationType = () => {
            if (typeof window.orientation !== "undefined") {
                const angle = Number(window.orientation);
                if (angle === 90) return "landscape-primary";
                if (angle === -90 || angle === 270) return "landscape-secondary";
                if (angle === 180) return "portrait-secondary";
                return "portrait-primary";
            }
            if (typeof window.innerWidth === "number" && typeof window.innerHeight === "number") {
                return window.innerHeight >= window.innerWidth ? "portrait-primary" : "landscape-primary";
            }
            return "portrait-primary";
        };

        const getAngle = () => {
            if (typeof window.orientation !== "undefined") {
                return Number(window.orientation) || 0;
            }
            if (typeof window.innerWidth === "number" && typeof window.innerHeight === "number") {
                return window.innerHeight >= window.innerWidth ? 0 : 90;
            }
            return 0;
        };

        const listeners = new Set();

        const orientationObj = {
            get type() {
                return getOrientationType();
            },
            get angle() {
                return getAngle();
            },
            get onchange() {
                return this._onchange || null;
            },
            set onchange(handler) {
                if (this._onchange) {
                    this.removeEventListener("change", this._onchange);
                }
                this._onchange = typeof handler === "function" ? handler : null;
                if (this._onchange) {
                    this.addEventListener("change", this._onchange);
                }
            },
            addEventListener(event, handler) {
                if (event === "change" && typeof handler === "function") {
                    listeners.add(handler);
                }
            },
            removeEventListener(event, handler) {
                if (event === "change") {
                    listeners.delete(handler);
                }
            },
            dispatchEvent(event) {
                listeners.forEach((fn) => {
                    try {
                        fn.call(orientationObj, event);
                    } catch (err) {
                        console.error("Error in screen.orientation change listener:", err);
                    }
                });
                return true;
            },
            lock() {
                return Promise.reject(new DOMException("screen.orientation.lock() is not supported", "NotSupportedError"));
            },
            unlock() {
                // No-op for unsupported platforms
            },
        };

        const notifyChange = () => {
            const event = typeof Event === "function" ? new Event("change") : { type: "change" };
            orientationObj.dispatchEvent(event);
        };

        window.addEventListener("orientationchange", notifyChange, { passive: true });
        window.addEventListener("resize", notifyChange, { passive: true });

        try {
            Object.defineProperty(window.screen, "orientation", {
                get: () => orientationObj,
                configurable: true,
                enumerable: true,
            });
        } catch {
            try {
                window.screen.orientation = orientationObj;
            } catch {
                if (typeof Screen !== "undefined" && Screen.prototype) {
                    Object.defineProperty(Screen.prototype, "orientation", {
                        get: () => orientationObj,
                        configurable: true,
                        enumerable: true,
                    });
                }
            }
        }
    }
}
